import axios from 'axios';

// API base URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

// Create axios instance
const apiService = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
apiService.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Inject X-Tenant-ID derived from host or explicit selection
    try {
      const host = window.location?.host || '';
      const parts = host.split('.');
      const inferredTenant = (parts.length > 2) ? parts[0] : null;
      const explicitTenant = localStorage.getItem('tenantId');
      const headerTenant = explicitTenant || inferredTenant;
      if (headerTenant) {
        config.headers = config.headers || {};
        config.headers['X-Tenant-ID'] = headerTenant;
      }
    } catch {}

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
apiService.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Company Management API
export const companyAPI = {
  // Company Info
  getCompanyInfo: () => apiService.get('/company/info'),
  updateCompanyInfo: (info) => apiService.put('/company/info', info),
  getCompanySettings: () => apiService.get('/company/settings'),
  updateCompanySettings: (settings) => apiService.put('/company/settings', settings),
};

// Dashboard API
export const dashboardAPI = {
  getStats: (period = 'month') => apiService.get(`/dashboard/stats?period=${period}`),
  getRecentActivities: (limit = 10) => apiService.get(`/dashboard/recent-activities?limit=${limit}`),
  getTopProducts: (period = 'month', limit = 5) => apiService.get(`/dashboard/top-products?period=${period}&limit=${limit}`),
  getRevenueChart: (period = 'month') => apiService.get(`/dashboard/revenue-chart?period=${period}`),
  getQuickActions: () => apiService.get('/dashboard/quick-actions'),
};

// User Management API
export const userAPI = {
  getUsers: () => apiService.get('/users'),
  getUser: (id) => apiService.get(`/users/${id}`),
  createUser: (user) => apiService.post('/users', user),
  updateUser: (id, user) => apiService.put(`/users/${id}`, user),
  deleteUser: (id) => apiService.delete(`/users/${id}`),
  
  // User Roles
  getUserRoles: (userId) => apiService.get(`/users/${userId}/roles`),
  assignUserRole: (userId, roleId) => apiService.post(`/users/${userId}/roles`, { roleId }),
  removeUserRole: (userId, roleId) => apiService.delete(`/users/${userId}/roles/${roleId}`),
  
  // User Permissions
  getUserPermissions: (userId) => apiService.get(`/users/${userId}/permissions`),
  updateUserPermissions: (userId, permissions) => apiService.put(`/users/${userId}/permissions`, permissions),
};

// Stock Management API
export const stockAPI = {
  // Stock Items
  getStockItems: () => apiService.get('/stock/items'),
  getStockItem: (id) => apiService.get(`/stock/items/${id}`),
  createStockItem: (item) => apiService.post('/stock/items', item),
  updateStockItem: (id, item) => apiService.put(`/stock/items/${id}`, item),
  deleteStockItem: (id) => apiService.delete(`/stock/items/${id}`),
  
  // Stock Movements
  getStockMovements: () => apiService.get('/stock/movements'),
  createStockMovement: (movement) => apiService.post('/stock/movements', movement),
  
  // Warehouses
  getWarehouses: () => apiService.get('/warehouses'),
  getWarehouse: (id) => apiService.get(`/warehouses/${id}`),
  createWarehouse: (warehouse) => apiService.post('/warehouses', warehouse),
  updateWarehouse: (id, warehouse) => apiService.put(`/warehouses/${id}`, warehouse),
  deleteWarehouse: (id) => apiService.delete(`/warehouses/${id}`),
  
  // Shelves
  getShelves: () => apiService.get('/shelves'),
  getShelf: (id) => apiService.get(`/shelves/${id}`),
  createShelf: (shelf) => apiService.post('/shelves', shelf),
  updateShelf: (id, shelf) => apiService.put(`/shelves/${id}`, shelf),
  deleteShelf: (id) => apiService.delete(`/shelves/${id}`),
  
  // Stock Counting
  getStockCounts: () => apiService.get('/stock/counts'),
  createStockCount: (count) => apiService.post('/stock/counts', count),
  completeStockCount: (id) => apiService.post(`/stock/counts/${id}/complete`),
};

// Sales & Purchase API
export const salesAPI = {
  // Invoices
  getInvoices: () => apiService.get('/invoices'),
  getInvoice: (id) => apiService.get(`/invoices/${id}`),
  createInvoice: (invoice) => apiService.post('/invoices', invoice),
  updateInvoice: (id, invoice) => apiService.put(`/invoices/${id}`, invoice),
  deleteInvoice: (id) => apiService.delete(`/invoices/${id}`),
  
  // Orders
  getOrders: () => apiService.get('/orders'),
  getOrder: (id) => apiService.get(`/orders/${id}`),
  createOrder: (order) => apiService.post('/orders', order),
  updateOrder: (id, order) => apiService.put(`/orders/${id}`, order),
  deleteOrder: (id) => apiService.delete(`/orders/${id}`),
  
  // Waybills
  getWaybills: () => apiService.get('/waybills'),
  getWaybill: (id) => apiService.get(`/waybills/${id}`),
  createWaybill: (waybill) => apiService.post('/waybills', waybill),
  updateWaybill: (id, waybill) => apiService.put(`/waybills/${id}`, waybill),
  deleteWaybill: (id) => apiService.delete(`/waybills/${id}`),
  
  // Purchases
  getPurchases: () => apiService.get('/purchases'),
  getPurchase: (id) => apiService.get(`/purchases/${id}`),
  createPurchase: (purchase) => apiService.post('/purchases', purchase),
  updatePurchase: (id, purchase) => apiService.put(`/purchases/${id}`, purchase),
  deletePurchase: (id) => apiService.delete(`/purchases/${id}`),
};

// Work Orders API
export const workOrderAPI = {
  getWorkOrders: () => apiService.get('/work-orders'),
  getWorkOrder: (id) => apiService.get(`/work-orders/${id}`),
  createWorkOrder: (workOrder) => apiService.post('/work-orders', workOrder),
  updateWorkOrder: (id, workOrder) => apiService.put(`/work-orders/${id}`, workOrder),
  deleteWorkOrder: (id) => apiService.delete(`/work-orders/${id}`),
  
  // Work Order Types
  getWorkOrderTypes: () => apiService.get('/work-order-types'),
  createWorkOrderType: (type) => apiService.post('/work-order-types', type),
  updateWorkOrderType: (id, type) => apiService.put(`/work-order-types/${id}`, type),
  deleteWorkOrderType: (id) => apiService.delete(`/work-order-types/${id}`),
};

// Payment & Collection API
export const paymentAPI = {
  // Payments
  getPayments: () => apiService.get('/payments'),
  getPayment: (id) => apiService.get(`/payments/${id}`),
  createPayment: (payment) => apiService.post('/payments', payment),
  updatePayment: (id, payment) => apiService.put(`/payments/${id}`, payment),
  deletePayment: (id) => apiService.delete(`/payments/${id}`),
  
  // Collections
  getCollections: () => apiService.get('/collections'),
  getCollection: (id) => apiService.get(`/collections/${id}`),
  createCollection: (collection) => apiService.post('/collections', collection),
  updateCollection: (id, collection) => apiService.put(`/collections/${id}`, collection),
  deleteCollection: (id) => apiService.delete(`/collections/${id}`),
  
  // Bank Transfers
  getBankTransfers: () => apiService.get('/bank-transfers'),
  getBankTransfer: (id) => apiService.get(`/bank-transfers/${id}`),
  createBankTransfer: (transfer) => apiService.post('/bank-transfers', transfer),
  updateBankTransfer: (id, transfer) => apiService.put(`/bank-transfers/${id}`, transfer),
  deleteBankTransfer: (id) => apiService.delete(`/bank-transfers/${id}`),
};

// Production API
export const productionAPI = {
  // Production Orders
  getProductionOrders: () => apiService.get('/production/orders'),
  getProductionOrder: (id) => apiService.get(`/production/orders/${id}`),
  createProductionOrder: (order) => apiService.post('/production/orders', order),
  updateProductionOrder: (id, order) => apiService.put(`/production/orders/${id}`, order),
  deleteProductionOrder: (id) => apiService.delete(`/production/orders/${id}`),
  
  // Production Recipes
  getProductionRecipes: () => apiService.get('/production/recipes'),
  getProductionRecipe: (id) => apiService.get(`/production/recipes/${id}`),
  createProductionRecipe: (recipe) => apiService.post('/production/recipes', recipe),
  updateProductionRecipe: (id, recipe) => apiService.put(`/production/recipes/${id}`, recipe),
  deleteProductionRecipe: (id) => apiService.delete(`/production/recipes/${id}`),
  
  // Work Stations
  getWorkStations: () => apiService.get('/work-stations'),
  getWorkStation: (id) => apiService.get(`/work-stations/${id}`),
  createWorkStation: (station) => apiService.post('/work-stations', station),
  updateWorkStation: (id, station) => apiService.put(`/work-stations/${id}`, station),
  deleteWorkStation: (id) => apiService.delete(`/work-stations/${id}`),
  
  // Quality Control
  getQualityControls: () => apiService.get('/quality-control'),
  getQualityControl: (id) => apiService.get(`/quality-control/${id}`),
  createQualityControl: (control) => apiService.post('/quality-control', control),
  updateQualityControl: (id, control) => apiService.put(`/quality-control/${id}`, control),
  deleteQualityControl: (id) => apiService.delete(`/quality-control/${id}`),
};

// B2B/B2C API
export const ecommerceAPI = {
  // Campaigns
  getCampaigns: () => apiService.get('/campaigns'),
  getCampaign: (id) => apiService.get(`/campaigns/${id}`),
  createCampaign: (campaign) => apiService.post('/campaigns', campaign),
  updateCampaign: (id, campaign) => apiService.put(`/campaigns/${id}`, campaign),
  deleteCampaign: (id) => apiService.delete(`/campaigns/${id}`),
  
  // Coupons
  getCoupons: () => apiService.get('/coupons'),
  getCoupon: (id) => apiService.get(`/coupons/${id}`),
  createCoupon: (coupon) => apiService.post('/coupons', coupon),
  updateCoupon: (id, coupon) => apiService.put(`/coupons/${id}`, coupon),
  deleteCoupon: (id) => apiService.delete(`/coupons/${id}`),
  
  // B2B Management
  getB2BSettings: () => apiService.get('/b2b/settings'),
  updateB2BSettings: (settings) => apiService.put('/b2b/settings', settings),
  
  // B2C Management
  getB2CSettings: () => apiService.get('/b2c/settings'),
  updateB2CSettings: (settings) => apiService.put('/b2c/settings', settings),
};

// Reports API
export const reportsAPI = {
  // General Reports
  getReports: () => apiService.get('/reports'),
  
  // Sales Reports
  getSalesReports: () => apiService.get('/reports/sales'),
  getSalesReport: (id) => apiService.get(`/reports/sales/${id}`),
  generateSalesReport: (params) => apiService.post('/reports/sales/generate', params),
  
  // Inventory Reports
  getInventoryReports: () => apiService.get('/reports/inventory'),
  getInventoryReport: (id) => apiService.get(`/reports/inventory/${id}`),
  generateInventoryReport: (params) => apiService.post('/reports/inventory/generate', params),
  
  // Financial Reports
  getFinancialReports: () => apiService.get('/reports/financial'),
  getFinancialReport: (id) => apiService.get(`/reports/financial/${id}`),
  generateFinancialReport: (params) => apiService.post('/reports/financial/generate', params),
  
  // Export Reports
  exportReport: (reportType, format) => apiService.get(`/reports/export/${reportType}`, {
    params: { format },
    responseType: 'blob'
  }),
};

// Consultant API
export const consultantAPI = {
  // Vehicle Inspection
  getVehicleInspections: () => apiService.get('/vehicle-inspections'),
  getVehicleInspection: (id) => apiService.get(`/vehicle-inspections/${id}`),
  createVehicleInspection: (inspection) => apiService.post('/vehicle-inspections', inspection),
  updateVehicleInspection: (id, inspection) => apiService.put(`/vehicle-inspections/${id}`, inspection),
  deleteVehicleInspection: (id) => apiService.delete(`/vehicle-inspections/${id}`),
  
  // OBD Integration
  getOBDData: (vehicleId) => apiService.get(`/obd/data/${vehicleId}`),
  connectOBD: (vehicleId) => apiService.post(`/obd/connect/${vehicleId}`),
  scanOBD: (vehicleId) => apiService.post(`/obd/scan/${vehicleId}`),
};

// Warehouse Terminal API
export const warehouseAPI = {
  // Picking Management
  getPickingLists: () => apiService.get('/picking/lists'),
  getPickingList: (id) => apiService.get(`/picking/lists/${id}`),
  startPicking: (listId) => apiService.post(`/picking/start/${listId}`),
  completePicking: (itemId) => apiService.post(`/picking/complete/${itemId}`),
  pausePicking: (listId) => apiService.post(`/picking/pause/${listId}`),
  
  // Location Transfer
  getLocationTransfers: () => apiService.get('/location-transfers'),
  createLocationTransfer: (transfer) => apiService.post('/location-transfers', transfer),
  completeLocationTransfer: (id) => apiService.post(`/location-transfers/${id}/complete`),
  
  // Terminal Operations
  searchProduct: (query) => apiService.get('/warehouse/product/search', { params: { q: query } }),
  getProductHistory: (productId) => apiService.get(`/warehouse/product/${productId}/history`),
  getProductStock: (productId) => apiService.get(`/warehouse/product/${productId}/stock`),
  
  // Printing
  printLabel: (data) => apiService.post('/warehouse/print/label', data),
  printReceipt: (data) => apiService.post('/warehouse/print/receipt', data),
  printReport: (data) => apiService.post('/warehouse/print/report', data),
};

// Customer Relations API
export const customerRelationsAPI = {
  // Customer Surveys
  getCustomerSurveys: () => apiService.get('/customer-surveys'),
  getCustomerSurvey: (id) => apiService.get(`/customer-surveys/${id}`),
  createCustomerSurvey: (survey) => apiService.post('/customer-surveys', survey),
  updateCustomerSurvey: (id, survey) => apiService.put(`/customer-surveys/${id}`, survey),
  deleteCustomerSurvey: (id) => apiService.delete(`/customer-surveys/${id}`),
  
  // Service Reminders
  getServiceReminders: () => apiService.get('/service-reminders'),
  getServiceReminder: (id) => apiService.get(`/service-reminders/${id}`),
  createServiceReminder: (reminder) => apiService.post('/service-reminders', reminder),
  updateServiceReminder: (id, reminder) => apiService.put(`/service-reminders/${id}`, reminder),
  deleteServiceReminder: (id) => apiService.delete(`/service-reminders/${id}`),
};

// HR Management API
export const hrAPI = {
  // Employees
  getEmployees: () => apiService.get('/employees'),
  getEmployee: (id) => apiService.get(`/employees/${id}`),
  createEmployee: (employee) => apiService.post('/employees', employee),
  updateEmployee: (id, employee) => apiService.put(`/employees/${id}`, employee),
  deleteEmployee: (id) => apiService.delete(`/employees/${id}`),
  
  // Attendance Tracking
  getAttendanceRecords: () => apiService.get('/attendance'),
  getAttendanceRecord: (id) => apiService.get(`/attendance/${id}`),
  createAttendanceRecord: (record) => apiService.post('/attendance', record),
  updateAttendanceRecord: (id, record) => apiService.put(`/attendance/${id}`, record),
  deleteAttendanceRecord: (id) => apiService.delete(`/attendance/${id}`),
  
  // Leave Management
  getLeaveRequests: () => apiService.get('/leave-requests'),
  getLeaveRequest: (id) => apiService.get(`/leave-requests/${id}`),
  createLeaveRequest: (request) => apiService.post('/leave-requests', request),
  updateLeaveRequest: (id, request) => apiService.put(`/leave-requests/${id}`, request),
  deleteLeaveRequest: (id) => apiService.delete(`/leave-requests/${id}`),
  approveLeaveRequest: (id) => apiService.post(`/leave-requests/${id}/approve`),
  rejectLeaveRequest: (id) => apiService.post(`/leave-requests/${id}/reject`),
};

// Integration Settings API
export const integrationAPI = {
  // POS Integration
  getPOSSettings: () => apiService.get('/integrations/pos'),
  updatePOSSettings: (settings) => apiService.put('/integrations/pos', settings),
  testPOSConnection: () => apiService.post('/integrations/pos/test'),
  
  // TecDoc Integration
  getTecDocSettings: () => apiService.get('/integrations/tecdoc'),
  updateTecDocSettings: (settings) => apiService.put('/integrations/tecdoc', settings),
  syncTecDoc: () => apiService.post('/integrations/tecdoc/sync'),
  
  // E-Invoice Integration
  getEInvoiceSettings: () => apiService.get('/integrations/einvoice'),
  updateEInvoiceSettings: (settings) => apiService.put('/integrations/einvoice', settings),
  testEInvoiceConnection: () => apiService.post('/integrations/einvoice/test'),
  
  // Accounting Integration
  getAccountingSettings: () => apiService.get('/integrations/accounting'),
  updateAccountingSettings: (settings) => apiService.put('/integrations/accounting', settings),
  testAccountingConnection: () => apiService.post('/integrations/accounting/test'),
};

// Print Templates API
export const printTemplatesAPI = {
  getPrintTemplates: () => apiService.get('/print-templates'),
  getPrintTemplate: (id) => apiService.get(`/print-templates/${id}`),
  createPrintTemplate: (template) => apiService.post('/print-templates', template),
  updatePrintTemplate: (id, template) => apiService.put(`/print-templates/${id}`, template),
  deletePrintTemplate: (id) => apiService.delete(`/print-templates/${id}`),
  
  // Template Types
  getInvoiceTemplate: () => apiService.get('/print-templates/invoice'),
  getReceiptTemplate: () => apiService.get('/print-templates/receipt'),
  getBarcodeTemplate: () => apiService.get('/print-templates/barcode'),
  getWaybillTemplate: () => apiService.get('/print-templates/waybill'),
  getShippingTemplate: () => apiService.get('/print-templates/shipping'),
};

export { apiService };
